import { BlockMetadata, PipelinePhase } from '@visualizer/types';
import { ContextHelper, StageContext } from './Context';
import { PipelineStage } from './Stage';
import { CachedBlockSnapshot, PipelineMemory } from './PipelineMemory';

import type { BlockParameters, Notification, SerializedBlock } from '@visualizer/types';
export type { BlockParameters };

/**
 * Abstract base class for pipeline blocks
 * Blocks persist but internally recreate stages when parameters change
 */
export abstract class PipelineBlock<T extends BlockParameters> {
    static metadata: BlockMetadata;

    readonly id: string;
    readonly name: string;
    readonly type: string;

    protected params: T;

    // Lazily created stages - recreated when params change
    private stages: PipelineStage<any>[];

    private cachedBlock = new PipelineMemory();

    protected notifications: Notification[] = [];

    constructor(params: T, id?: string) {
        this.id = id || crypto.randomUUID();
        this.name = (this.constructor as typeof PipelineBlock).metadata.name;
        this.type = (this.constructor as typeof PipelineBlock).metadata.type;

        this.params = params;
        if (this.params.enabled === undefined) {
            this.enable();
        }
        this.stages = this.defineStages();
    }

    /**
     * Update parameters - marks stages as stale for recreation
     */
    public updateParams<T>(params: Partial<T>): void {
        const didChange = Object.keys(params).some(
            (key) => (this.params as Record<string, unknown>)[key] !== (params as Record<string, unknown>)[key]
        );
        this.params = { ...this.params, ...params };
        if (didChange) {
            this.cachedBlock.clear();
        }
    }

    /**
     * Get params (immutable to prevent external modification)
     */
    public getParams(): Readonly<T> {
        return this.params;
    }

    /**
     * Get stages - recreates if parameters changed
     */
    private getStages(): PipelineStage<any>[] {
        return this.stages;
    }

    /**
     * Used to define the hardcoded and fixed stages for this block
     */
    protected abstract defineStages(): PipelineStage<any>[];

    /**
     * Call it to refresh the stages!
     */
    protected updateStages(): void {
        this.stages = this.defineStages();
    }

    /**
     * Can be overriden and used to decide how stages are execute within the block.
     */
    protected executeStages(stages: PipelineStage<any>[], context: StageContext): StageContext {
        let localContext = context;
        for (const stage of stages) {
            localContext = stage.execute(localContext, this.params);
        }
        return localContext;
    }

    /**
     * Execute the block - processes all stages with local context
     */
    public execute(globalContext: StageContext): { context: StageContext; usedCache: boolean } {
        // Restore context from cache, if restore fails clear cache:
        if (this.cachedBlock.get()) {
            const restoredContext = this.cachedBlock.restoreContext(globalContext);
            if (restoredContext) {
                // console.log(`Cache hit for block ${this.name}`);
                return { context: restoredContext, usedCache: true };
            }
            console.log(`Cache restore failed for block ${this.name}, executing stages`);
            this.cachedBlock.clear();
        }

        this.clearNotifications();

        let localContext = ContextHelper.create(
            globalContext.sensors,
            globalContext.absoluteMinMax,
            globalContext.remappedMinMax,
            globalContext.octTree
        );

        localContext = this.executeStages(this.getStages(), localContext);

        // Merge local context data back to global context
        const outputContext = {
            sensors: localContext.sensors,
            data: new Map(),
            absoluteMinMax: localContext.absoluteMinMax,
            remappedMinMax: localContext.remappedMinMax,
            octTree: globalContext.octTree,
        };

        this.cachedBlock.storeContext(globalContext, outputContext);

        // Register notifications:
        for (const stage of this.getStages()) this.notifications = [...this.notifications, ...stage.getNotifications()];

        return { context: outputContext, usedCache: false };
    }

    public getNotifications() {
        const notifications = this.notifications;
        return notifications;
    }
    public clearNotifications() {
        this.notifications = [];
    }

    public enable() {
        this.params.enabled = true;
    }

    public disable() {
        this.params.enabled = false;
    }

    public isEnabled(): boolean {
        return this.params?.enabled ?? true;
    }

    public getCachedContext(): CachedBlockSnapshot | undefined {
        return this.cachedBlock.getCachedContext();
    }

    public clearCache(): void {
        this.cachedBlock.clear();
    }

    protected serializeParams(): T {
        return this.params;
    }

    public toJSON(): SerializedBlock {
        return {
            id: this.id,
            type: this.type,
            name: this.name,
            params: this.serializeParams(),
        };
    }

    public getPipelinePhase(): PipelinePhase {
        const metadata = (this.constructor as typeof PipelineBlock).metadata;
        return metadata?.pipelinePhase ?? PipelinePhase.Regular;
    }
}
